/* eslint-disable */
/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _|
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|

=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
const UAParser = require('ua-parser-js');
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  FormErrorMessage,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { Field, Form, Formik } from 'formik';
// Layout
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/auth.jpg";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

import AuthService from "services/auth.services.js";
import { useAuth } from '../../../authProvider';

function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);
  const history = useHistory()
  const [loginError, setLoginError] = useState("");
  const isMounted = useRef(true);
  const { signIn } = useAuth();

  // add cleanup for unmounted component
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Validate login form
  function validateLogin(email) {
    let error
    if (!email) {
      error = 'email or password is required'
    }
    return error
  }
  // Get device info
  const getDeviceInfo = () => {
    const parser = new UAParser();
    const result = parser.getResult();
    return `${result.browser.name} on ${result.device.model}`;
  };
  // Get user location
  const getLocation = () => {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn('Location request timed out, returning empty coordinates');
        resolve({
          lat: "",
          long: ""
        });
      }, 3000); // 20 second timeout

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          resolve({
            lat: position.coords.latitude,
            long: position.coords.longitude
          });
        },
        (error) => {
          clearTimeout(timeoutId);
          console.warn('Geolocation error:', error.message);
          resolve({
            lat: "",
            long: ""
          });
        }
      );
    });
  };
  // Handle form submit
  const handleSubmit = async (values, actions) => {
    try {
      const location = await getLocation();
      const ipAddress = await AuthService.getIPAddress();
      const deviceInfo = getDeviceInfo();

      const loginData = {
        email: values.email,
        password: values.password,
        device_loc_lat: location.lat.toString(),
        device_loc_long: location.long.toString(),
        ip_address: ipAddress,
        device_info: deviceInfo
      };
      // Call login service
      await signIn(loginData);
      // Redirect to OTP page
      if (isMounted.current) {
        setLoginError(""); // Clear any existing errors
        history.push('/auth/otp');
      }
    } catch (error) {
      if (isMounted.current) {
        // Always use the error message from backend response if available
        const errorMessage = error.response?.data?.message || error.message || "An error occurred";
        setLoginError(errorMessage);
        console.error("Login error:", error);
      }
    } finally {
      if (isMounted.current) {
        actions.setSubmitting(false);
      }
    }
  };
  // Handle input change
  const handleInputChange = (setFieldValue, field, value) => {
    setLoginError(""); // Clear error message when user types
    setFieldValue(field, value);
  };


  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Sign In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>

          <Formik
            // initialValues={{ email: "admin@gmail.com", password: "secret" }}
            initialValues={{}}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <Field name="email" validate={validateLogin}>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.email && form.touched.email}>
                      <FormLabel
                        htmlFor="email"
                        display='flex'
                        ms='4px'
                        placeholder='Enter Email'
                        fontSize='sm'
                        fontWeight='500'
                        color={textColor}
                        mb='8px'>
                        Email<Text color={brandStars}>*</Text>
                      </FormLabel>
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      <Input
                        {...field}
                        type="email"
                        id="id-email"
                        variant='auth'
                        fontSize='sm'
                        ms={{ base: "0px", md: "0px" }}
                        mb='24px'
                        fontWeight='500'
                        size='lg'
                        value={field.value || ''} // Add this to ensure value is never undefined
                        onChange={(e) => handleInputChange(form.setFieldValue, "email", e.target.value)}
                      />
                    </FormControl>
                  )}
                </Field>
                <Field name="password" validate={validateLogin}>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.password && form.touched.password}>
                      <FormLabel
                        htmlFor="password"
                        ms='4px'
                        fontSize='sm'
                        fontWeight='500'
                        color={textColor}
                        display='flex'>
                        Password<Text color={brandStars}>*</Text>
                      </FormLabel>
                      <InputGroup size='md'>
                        <Input
                          {...field}
                          fontSize='sm'
                          id="id-password"
                          placeholder='Enter Password'
                          mb='24px'
                          size='lg'
                          type={show ? "text" : "password"}
                          variant='auth'
                          value={field.value || ''} // Add this to ensure value is never undefined
                          onChange={(e) => handleInputChange(form.setFieldValue, "password", e.target.value)}
                        />
                        <InputRightElement display='flex' alignItems='center' mt='4px'>
                          <Icon
                            color={textColorSecondary}
                            _hover={{ cursor: "pointer" }}
                            as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                            onClick={handleClick}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>
                  )}
                </Field>
                {loginError && (
                  <Alert status="error" mb="24px" borderRadius="8px">
                    <AlertIcon />
                    {loginError}
                  </Alert>
                )}
                {/* kode dibawah untuk phase 2 */}
                {/* <Flex justifyContent='space-between' align='center' mb='24px'>
                <FormControl display='flex' alignItems='center'>
                  <Checkbox
                    id='remember-login'
                    colorScheme='brandScheme'
                    me='10px'
                  />
                  <FormLabel
                    htmlFor='remember-login'
                    mb='0'
                    fontWeight='normal'
                    color={textColor}
                    fontSize='sm'>
                    Keep me logged in
                  </FormLabel>
                </FormControl>
                <NavLink to='/auth/forgot-password'>
                  <Text
                    color={textColorBrand}
                    fontSize='sm'
                    w='124px'
                    fontWeight='500'>
                    Forgot password?
                  </Text>
                </NavLink>
              </Flex> */}
                <Button
                  fontSize='sm'
                  variant='brand'
                  fontWeight='500'
                  w='100%'
                  h='50'
                  mb='24px'
                  isLoading={props.isSubmitting}
                  type="submit">
                  Sign In
                </Button>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
